// src/admin/components/UserPermission.js
import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import config from '../../config';
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Box,
  CircularProgress,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  Button,
  FormControl,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from '@mui/material';

/* ✅ MENU LIST */
const MENU_LIST = [
  { key: 'dashboard', label: 'Dashboard' },
  { key: 'hotels', label: 'Hotels' },
  { key: 'hotels/add', label: 'Add Hotel' },
  { key: 'rooms', label: 'Rooms' },
  { key: 'rooms/add', label: 'Add Room' },
  { key: 'roomtype', label: 'Room Type Master' },
  { key: 'manageinventory', label: 'Manage Inventory' },
  { key: 'managepackage', label: 'Manage Package' },
  { key: 'banquethall/add', label: 'Add Banquet Hall' },
  { key: 'availableroom', label: 'Available Room' },
  { key: 'packagereport', label: 'Package Report' },
  { key: 'BookingReport', label: 'Booking Report' },
  { key: 'PettyCashManagement', label: 'Petty Cash Management' },
  { key: 'Addonm', label: 'Addon Master' },
  { key: 'Paymentreport', label: 'Payment Report' },
  { key: 'Bookingfilter', label: 'Booking Filter' },
  { key: 'AddonProfitReport', label: 'Addon Profit Report' },
  { key: 'InvoiceReport', label: 'Invoice Report' },
  { key: 'Guestreport', label: 'Guest Report' },
  { key: 'CreateUser', label: 'Create User' },
  { key: 'UserPermission', label: 'User Permission' },
  { key: 'UserReport', label: 'User Report' },
  { key: 'Agent', label: 'Agent Master' },
  { key: 'AgentReport', label: 'Agent Report' },
  { key: 'Couponmaster', label: 'Couponmaster' },
  { key: 'restaurant', label: 'restaurant' },
  { key: 'Category', label: 'Category' },
  { key: 'AddItem', label: 'AddItem' },
  { key: 'CreateOrder', label: 'CreateOrder' },
  { key: 'Order', label: 'Order' },
  { key: 'AllCategory', label: 'AllCategory' },
  { key: 'ItemReport', label: 'ItemReport' },
  { key: 'RestaurantInvoiceReport', label: 'Restaurant Invoice Report' },
  { key: 'RestaurantGuestReport', label: 'Restaurant Guests' },
  { key: 'Changeroom', label: 'Changeroom' },
  { key: 'Createarea', label: 'Createarea' },
  { key: 'TableOrders', label: 'TableOrders' },
  { key: 'RestaurantOrder', label: 'RestaurantOrder' },
  { key: 'ManageUser', label: 'ManageUser' },
  { key: 'ManageAgent', label: 'ManageAgent' },
  { key: 'QuickBill', label: 'QuickBill' },
  { key: 'Customerwallet', label: 'Customerwallet' },
  { key: 'KitchenPendingOrders', label: 'KitchenPendingOrders' },
  { key: 'AreaPricing', label: 'Area Pricing' },
  { key: 'KotReport', label: 'KOT Report' },
  { key: 'HourlyBooking', label: 'Hourly Booking' },
  { key: 'QuickBooking', label: 'Quick Booking' },
  { key: 'Reservations', label: 'Table Reservations' },
  { key: 'MenuMaster', label: 'Menu Management' },
  { key: 'SupportTickets', label: 'Support / Complaints' },
];
const company = JSON.parse(sessionStorage.getItem('companyDetails'));
export default function UserPermission() {
  const [users, setUsers] = useState([]);
  const [permissions, setPermissions] = useState({});
  const [loading, setLoading] = useState(true);

  const [open, setOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  // DB-driven menus (MenuMaster) merged on top of the static MENU_LIST, so any
  // menu added from "Menu Management" also becomes grantable here — no code change.
  const [dbMenus, setDbMenus] = useState([]);
  // Plan-wise cap: the admin can only grant menus included in the current plan
  // (trial / all-menus plan = no cap). null = not loaded yet.
  const [planAccess, setPlanAccess] = useState(null);
  // Edit-user (name / password / contact) dialog state.
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState(null);
  const [editSaving, setEditSaving] = useState(false);

  const token = sessionStorage.getItem('token');

  const openEditUser = (u) => {
    setEditForm({
      userId: u.id,
      name: u.name || '',
      username: u.username || '',
      password: '', // blank = keep existing
      phone: u.phone || '',
      email: u.email || '',
    });
    setEditOpen(true);
  };

  const submitEditUser = async () => {
    if (!editForm?.name?.trim()) {
      toast.error('Name is required');
      return;
    }
    setEditSaving(true);
    try {
      const res = await fetch(`${config.API_BASE_URL}/useraccount/update`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          userId: editForm.userId,
          companyId: company?.id,
          name: editForm.name,
          username: editForm.username,
          password: editForm.password, // blank → backend keeps old password
          phone: editForm.phone,
          email: editForm.email,
        }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok || body?.success === false) {
        toast.error(body?.error || 'Failed to update user');
        return;
      }
      toast.success('User updated');
      setEditOpen(false);
      // Refresh the user list so the new name shows.
      fetch(
        `${config.API_BASE_URL}/Home/GetAllUsers/${encodeURIComponent(company?.id)}`,
        { headers: { Authorization: `Bearer ${token}` } },
      )
        .then((r) => r.json())
        .then((data) => {
          const list = Array.isArray(data) ? data : [];
          setUsers([...new Map(list.map((u) => [u.id, u])).values()]);
        })
        .catch(() => {});
    } catch {
      toast.error('Failed to update user');
    } finally {
      setEditSaving(false);
    }
  };

  // The grantable menu list = built-in static + DB menus, then CAPPED to the
  // company's current plan (trial / all-menus plan = no cap).
  const fullMenuList = [...MENU_LIST, ...dbMenus];
  const menuList =
    !planAccess || planAccess.allMenus
      ? fullMenuList
      : fullMenuList.filter((m) => (planAccess.menuKeys || []).includes(m.key));

  // Load the company's plan-wise menu access.
  useEffect(() => {
    if (!company?.id) return;
    fetch(`${config.API_BASE_URL}/Subscription/company-menus/${company.id}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.json())
      .then((d) => setPlanAccess(d || { allMenus: true }))
      .catch(() => setPlanAccess({ allMenus: true }));
  }, []);

  useEffect(() => {
    if (!company?.id) return;
    fetch(`${config.API_BASE_URL}/menu/all/${company.id}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.json())
      .then((d) => {
        if (!Array.isArray(d)) return;
        const existing = new Set(MENU_LIST.map((m) => m.key));
        const seen = new Set();
        const extra = d
          .filter((m) => m.menuKey && !existing.has(m.menuKey))
          .filter((m) => (seen.has(m.menuKey) ? false : seen.add(m.menuKey)))
          .map((m) => ({ key: m.menuKey, label: m.text || m.menuKey }));
        setDbMenus(extra);
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ================= FETCH USERS ================= */
  useEffect(() => {
    fetch(
      `${config.API_BASE_URL}/Home/GetAllUsers/${encodeURIComponent(company?.id)}`,
      {
        headers: { Authorization: `Bearer ${token}` },
      },
    )
      .then((res) => res.json())
      .then((data) => {
        console.log('GetAllUsers API response:', data); // 👈 FULL RESULT

        const list = Array.isArray(data) ? data : [];
        const uniqueUsers = [...new Map(list.map((u) => [u.id, u])).values()];
        setUsers(uniqueUsers);

        const perms = {};
        uniqueUsers.forEach((u) => {
          perms[u.id] = u.menuKeys
            ? u.menuKeys
                .split(',')
                .map((k) => k.trim())
                .filter(Boolean)
            : [];
        });
        setPermissions(perms);
      })
      .catch((err) => {
        console.error('GetAllUsers API error:', err);
        toast.error('Failed to load users');
      })
      .finally(() => setLoading(false));
  }, [token]);

  /* ================= HANDLE CHANGE ================= */
  const handlePermissionChange = (userId, values) => {
    setPermissions((prev) => ({
      ...prev,
      [userId]: values,
    }));
  };

  /* ================= SAVE ================= */
  const savePermission = async () => {
    try {
      const res = await fetch(
        `${config.API_BASE_URL}/home/SaveUserPermission`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            UserId: selectedUser.id,
            menus: permissions[selectedUser.id] || [],
          }),
        },
      );

      res.ok
        ? toast.success('Permission saved!')
        : toast.error('Save failed');
      setOpen(false);
    } catch {
      toast.error('Error saving permission');
    }
  };

  if (loading) {
    return (
      <Box
        display='flex'
        justifyContent='center'
        alignItems='center'
        minHeight='60vh'>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 4 }}>
      <Typography variant='h4' fontWeight='bold' mb={4} textAlign='center'>
        👥 User Permission Management
      </Typography>

      <Paper elevation={3}>
        <TableContainer sx={{ maxHeight: '75vh' }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                {['#', 'Name', 'Username', 'Mobile', 'Menus', 'Action'].map(
                  (h) => (
                    <TableCell key={h} sx={{ fontWeight: 'bold' }}>
                      {h}
                    </TableCell>
                  ),
                )}
              </TableRow>
            </TableHead>

            <TableBody>
              {users.map((u, i) => (
                <TableRow key={u.id}>
                  <TableCell>{i + 1}</TableCell>
                  <TableCell>{u.name || '-'}</TableCell>
                  <TableCell>{u.username}</TableCell>
                  <TableCell>{u.phone || '-'}</TableCell>

                  <TableCell>
                    {(permissions[u.id] || [])
                      .map(
                        (k) => menuList.find((m) => m.key === k)?.label || k,
                      )
                      .join(', ')}
                  </TableCell>

                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Button
                        size='small'
                        variant='outlined'
                        onClick={() => {
                          setSelectedUser(u);
                          setOpen(true);
                        }}>
                        Edit Menus
                      </Button>
                      <Button
                        size='small'
                        variant='outlined'
                        color='secondary'
                        onClick={() => openEditUser(u)}>
                        Edit User
                      </Button>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* ✅ DIALOG (NO BUGS EVER) */}
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        fullWidth
        maxWidth='sm'>
        <DialogTitle>Manage Menus – {selectedUser?.username}</DialogTitle>

        <DialogContent>
          <FormControl fullWidth size='small' sx={{ mt: 2 }}>
            <InputLabel>Select Menus</InputLabel>
            <Select
              multiple
              value={permissions[selectedUser?.id] || []}
              onChange={(e) =>
                handlePermissionChange(selectedUser.id, e.target.value)
              }
              label='Select Menus'
              renderValue={(selected) =>
                selected
                  .map((k) => menuList.find((m) => m.key === k)?.label || k)
                  .join(', ')
              }>
              {menuList.map((menu) => (
                <MenuItem key={menu.key} value={menu.key}>
                  <Checkbox
                    checked={(permissions[selectedUser?.id] || []).includes(
                      menu.key,
                    )}
                  />
                  <ListItemText primary={menu.label} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button variant='contained' onClick={savePermission}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── EDIT USER (name / password / contact) ── */}
      <Dialog
        open={editOpen}
        onClose={() => setEditOpen(false)}
        fullWidth
        maxWidth='xs'>
        <DialogTitle>Edit User – {editForm?.username}</DialogTitle>
        <DialogContent
          sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
          <TextField
            label='Name'
            value={editForm?.name || ''}
            onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
            fullWidth
            sx={{ mt: 1 }}
          />
          <TextField
            label='Username'
            value={editForm?.username || ''}
            onChange={(e) =>
              setEditForm({ ...editForm, username: e.target.value })
            }
            fullWidth
          />
          <TextField
            label='New Password (blank = keep current)'
            type='text'
            value={editForm?.password || ''}
            onChange={(e) =>
              setEditForm({ ...editForm, password: e.target.value })
            }
            fullWidth
          />
          <TextField
            label='Phone'
            value={editForm?.phone || ''}
            onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
            fullWidth
          />
          <TextField
            label='Email'
            value={editForm?.email || ''}
            onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
            fullWidth
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)}>Cancel</Button>
          <Button
            variant='contained'
            onClick={submitEditUser}
            disabled={editSaving}>
            {editSaving ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
